/**
 * BriefNav — sticky pill-nav at top of #brief-panel that auto-detects
 * top-of-brief sections and offers one-tap scroll to each.
 *
 * Phase: BEACON-78 (post BEACON-76/77 brief quality-leveling). The 5
 * non-Pac/OM briefs gained `#one-page-strategy` + `#wind-matrix` anchors
 * in BEACON-76/77; Pac and OM had section headings but pandoc auto-IDs
 * (`#one-page-strategy-read-this-before-the-round` etc.). This module
 * resolves both shapes uniformly.
 *
 * Detection strategy (in order of preference):
 *   1. Explicit anchor IDs from BEACON-76/77 fold:
 *      `#one-page-strategy`, `#wind-matrix`, `#quick-ref-card`,
 *      `#round-1-fri-am-recon`, `#round-2-mon-pm-closer`, `#t0-update-mechanism`
 *   2. Heading-text pattern match (covers Pac/OM and other untouched briefs):
 *      `/one[\s-]page strategy/i`, `/wind[\s-]matrix/i`,
 *      `/quick[\s-]ref(erence)?[\s-]card/i`
 *
 * Renders a horizontal pill row at the top of `#brief-panel`. Each pill
 * scrolls its target into view (respecting prefers-reduced-motion). In
 * `?mode=round` the pills compact to icon-only (still tappable thumb-zone
 * size). The nav element auto-hides if fewer than 2 pills are detected
 * (i.e. no value over a passive scroll).
 *
 * The nav is rebuilt on every brief load (course switch / scenario
 * change), and disposed cleanly so it doesn't leak event listeners.
 */

const SECTION_DEFS = [
  // Order matters — this is the rendered pill order.
  {
    key: "strategy",
    label: "Strategy",
    icon: "🎯",
    aria: "Jump to one-page strategy",
    explicitIds: ["one-page-strategy"],
    headingRegex: /one[\s\u2010\u2011\u2012\u2013-]+page\s+strategy/i,
  },
  {
    key: "wind-matrix",
    label: "Wind",
    icon: "🌬️",
    aria: "Jump to wind matrix",
    explicitIds: ["wind-matrix"],
    headingRegex: /wind[\s\u2010\u2011\u2012\u2013-]+matrix/i,
  },
  {
    key: "round-1",
    label: "R1",
    icon: "1\uFE0F\u20E3", // 1️⃣ — keycap-1 — distinguishable from R2 even in compact icon-only mode
    aria: "Jump to round 1 pre-round protocol",
    explicitIds: ["round-1-fri-am-recon"],
    headingRegex: null, // BD-only; no fuzzy match needed
  },
  {
    key: "round-2",
    label: "R2",
    icon: "2\uFE0F\u20E3", // 2️⃣ — keycap-2
    aria: "Jump to round 2 pre-round protocol",
    explicitIds: ["round-2-mon-pm-closer"],
    headingRegex: null,
  },
  {
    key: "holes",
    label: "Holes",
    icon: "⛳",
    aria: "Jump to hole-by-hole",
    explicitIds: [],
    // Catches "HOLE-BY-HOLE", "Hole-by-hole intel", "RF's bag on this course"
    headingRegex: /hole[\s\u2010\u2011\u2012\u2013-]+by[\s\u2010\u2011\u2012\u2013-]+hole|bag on this course/i,
  },
  {
    key: "quick-ref",
    label: "Card",
    icon: "📋",
    aria: "Jump to quick-reference card",
    explicitIds: ["quick-ref-card"],
    headingRegex: /quick[\s\u2010\u2011\u2012\u2013-]+ref(?:erence)?[\s\u2010\u2011\u2012\u2013-]+card/i,
  },
  {
    key: "t0-update",
    label: "T-0",
    icon: "🌦️",
    aria: "Jump to T-0 update mechanism",
    explicitIds: ["t0-update-mechanism"],
    headingRegex: null,
  },
];

const NAV_ID = "brief-nav";
const MIN_PILLS_TO_RENDER = 2;

export class BriefNav {
  /**
   * @param {Object} opts
   * @param {HTMLElement} opts.panel - #brief-panel root
   * @param {HTMLElement} [opts.mountPoint] - where to inject nav. Defaults to panel.
   * @param {boolean} [opts.compact=false] - render in compact (icon-only) mode
   */
  constructor({ panel, mountPoint = null, compact = false } = {}) {
    if (!panel) throw new Error("BriefNav: panel required");
    this.panel = panel;
    this.mountPoint = mountPoint || panel;
    this.compact = Boolean(compact);
    this._navEl = null;
    this._onClick = (ev) => this._handleClick(ev);
  }

  /**
   * Scan the brief panel for known sections and render the nav. Safe to
   * call repeatedly (it disposes any existing nav first).
   * Returns the count of pills rendered (0 if hidden).
   */
  render() {
    this.dispose();

    const sections = this._discoverSections();
    if (sections.length < MIN_PILLS_TO_RENDER) {
      return 0;
    }

    const nav = document.createElement("nav");
    nav.id = NAV_ID;
    nav.className = "brief-nav" + (this.compact ? " brief-nav--compact" : "");
    nav.setAttribute("aria-label", "Brief sections");

    for (const sec of sections) {
      const pill = document.createElement("button");
      pill.type = "button";
      pill.className = "brief-nav-pill";
      pill.dataset.section = sec.key;
      pill.dataset.targetId = sec.targetId;
      pill.setAttribute("aria-label", sec.aria);
      pill.innerHTML = `<span class="brief-nav-pill-icon" aria-hidden="true">${sec.icon}</span><span class="brief-nav-pill-label">${sec.label}</span>`;
      nav.appendChild(pill);
    }

    nav.addEventListener("click", this._onClick);

    // Inject at top of mount point. We use prepend() so it stays the first
    // child of `#brief-panel`, becoming the sticky-top section.
    this.mountPoint.prepend(nav);
    this._navEl = nav;

    console.info(`[BriefNav] rendered ${sections.length} pills: ${sections.map((s) => s.key).join(", ")}`);
    return sections.length;
  }

  /** Toggle compact (icon-only) mode after initial render. */
  setCompact(compact) {
    this.compact = Boolean(compact);
    if (this._navEl) {
      this._navEl.classList.toggle("brief-nav--compact", this.compact);
    }
  }

  /** Remove the nav element + click listener (idempotent). */
  dispose() {
    if (this._navEl) {
      this._navEl.removeEventListener("click", this._onClick);
      this._navEl.remove();
      this._navEl = null;
    }
  }

  /**
   * Walk the brief panel and produce an ordered list of sections to render.
   * Each entry has `{ key, label, icon, aria, targetId }`. We pick the
   * first heading we find for each definition (first explicit-ID match,
   * else first heading-regex match).
   */
  _discoverSections() {
    const found = [];
    const headings = Array.from(
      this.panel.querySelectorAll("h1[id], h2[id], h3[id], h4[id]")
    );

    for (const def of SECTION_DEFS) {
      let targetId = null;

      // Pass 1: explicit IDs
      for (const id of def.explicitIds) {
        if (this.panel.querySelector(`#${CSS.escape(id)}`)) {
          targetId = id;
          break;
        }
      }

      // Pass 2: heading-text regex match
      if (!targetId && def.headingRegex) {
        for (const h of headings) {
          // Skip the brief-nav itself if somehow it has a heading
          if (h.closest(`#${NAV_ID}`)) continue;
          const text = (h.textContent || "").trim();
          if (def.headingRegex.test(text)) {
            targetId = h.id;
            break;
          }
        }
      }

      if (targetId) {
        found.push({
          key: def.key,
          label: def.label,
          icon: def.icon,
          aria: def.aria,
          targetId,
        });
      }
    }

    return found;
  }

  _handleClick(ev) {
    const pill = ev.target.closest(".brief-nav-pill");
    if (!pill) return;
    ev.preventDefault();
    const targetId = pill.dataset.targetId;
    if (!targetId) return;
    const target = this.panel.querySelector(`#${CSS.escape(targetId)}`);
    if (!target) return;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const behavior = prefersReducedMotion ? "auto" : "smooth";

    target.scrollIntoView({ behavior, block: "start" });

    // Move focus for screen-reader users (target is visible after scroll).
    // Use tabindex=-1 to allow programmatic focus without making it a tab stop.
    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });

    // Visually mark the pill as active for ~600ms — a tiny bit of feedback
    // since scroll alone is easy to miss in a mobile context.
    pill.classList.add("brief-nav-pill--active");
    window.setTimeout(() => pill.classList.remove("brief-nav-pill--active"), 600);
  }
}

export default BriefNav;
